import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
    addAutoPaymentService,
    addMakePaymentService,
    addPaymentSourceService,
    autoPaymentService,
    deleteAutoPaymentService,
    deletePaymentSourceService,
    makeDefaultPaymentService,
    paymentSourceAlternateService,
    paymentSourceService,
    updatePaymentNameDescriptionService,
} from '../../../services/userServices';

import { CreatePayment, UpdatePaymentNameDescriptionArgs } from './types';
import { CreateAutoPayArgs, CreatePaymentArgs, MakeExistingPaymentArgs, PaymentState } from 'MyAccountPlus-UIComponents';

interface InitialPaymentState extends PaymentState {
    makePaymentSuccess: boolean;
}

const initialState: InitialPaymentState = {
    loading: false,
    paymentLoader: false,
    makePaymentSuccess: false,
    error: "",
    success: "",
    autoPayment: [],
    paymentSource: [],
    alternatePaymentSource: [],
};

export const paymentSourceThunk = createAsyncThunk("paymentSlice/paymentSourceThunk", async (args: string, thunkAPI) => {
    try {
        const { data } = await paymentSourceService(args);
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data?.error);
    }
});

export const autoPaymentThunk = createAsyncThunk("paymentSlice/autoPaymentThunk", async (_, thunkAPI) => {
    try {
        const { data } = await autoPaymentService();
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data?.error);
    }
});

export const alternatePaymentSourceThunk = createAsyncThunk("paymentSlice/alternatePaymentSourceThunk", async (args: string, thunkAPI) => {
    try {
        const { data } = await paymentSourceAlternateService(args);
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data?.error);
    }
});

export const addPaymentSourceThunk = createAsyncThunk("paymentSlice/addPaymentSourceThunk", async (args: CreatePayment, thunkAPI) => {
    try {
        const { data } = await addPaymentSourceService(args);
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        const errorCode = error.response.data?.error?.error_code;
        const errorDetail = error.response.data?.detail;
        // If error code exists, return. otherwise, return detail message
        const errorMessage = errorCode
            ? errorCode
            : errorDetail
                ? errorDetail
                : "An error occurred.";

        // Reject with the extracted error message
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const deletePaymentSourceThunk = createAsyncThunk("paymentSlice/deletePaymentSourceThunk", async (args: { user_id: string, payment_id: string }, thunkAPI) => {
    try {
        const { data } = await deletePaymentSourceService(args);
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data?.error);
    }
});

export const updatePaymentNameDescriptionThunk = createAsyncThunk("paymentSlice/updatePaymentNameDescriptionThunk", async (args: UpdatePaymentNameDescriptionArgs, thunkAPI) => {
    try {
        const { payment_id, ...payload } = args;
        const { data } = await updatePaymentNameDescriptionService(payload, args.payment_id!);
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data);
    }
});

export const addMakePaymentThunk = createAsyncThunk("paymentSlice/addMakePaymentThunk", async (args: CreatePaymentArgs | MakeExistingPaymentArgs, thunkAPI) => {
    try {
        const { data } = await addMakePaymentService(args);
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data);
    }
});

export const addAutoPaymentThunk = createAsyncThunk("paymentSlice/addAutoPaymentThunk", async (args: CreateAutoPayArgs, thunkAPI) => {
    try {
        const { data } = await addAutoPaymentService(args);
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data?.error);
    }
});

export const deleteAutoPaymentThunk = createAsyncThunk("paymentSlice/deleteAutoPaymentThunk", async (args: { user_id: string, payment_id: string }, thunkAPI) => {
    try {
        const { data } = await deleteAutoPaymentService(args);
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data?.error);
    }
});

export const makeDefaultPaymentThunk = createAsyncThunk("paymentSlice/makeDefaultPaymentThunk", async (args: { user_id: string, payment_id: string }, thunkAPI) => {
    try {
        const { data } = await makeDefaultPaymentService(args);
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data?.error);
    }
});

export const paymentSlice = createSlice({
    name: 'paymentSlice',
    initialState,
    reducers: {
        errorReducer: (state) => {
            state.error = ""
        },
        clearState: (state) => {
            return {
                ...state,
                ...initialState
            }
        },
        successReducer: (state) => {
            state.success = ""
        },
        makePaymentSuccessReducer: (state) => {
            state.makePaymentSuccess = false
        },
    },
    extraReducers(builder) {
        builder
            .addCase(paymentSourceThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(paymentSourceThunk.fulfilled, (state, action) => {
                state.paymentSource = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(paymentSourceThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(autoPaymentThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(autoPaymentThunk.fulfilled, (state, action) => {
                state.autoPayment = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(autoPaymentThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(alternatePaymentSourceThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(alternatePaymentSourceThunk.fulfilled, (state, action) => {
                state.alternatePaymentSource = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(alternatePaymentSourceThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(addPaymentSourceThunk.pending, (state, action) => {
                state.paymentLoader = true
            })
            .addCase(addPaymentSourceThunk.fulfilled, (state, action) => {
                state.paymentLoader = false
                state.success = action.payload.data.message
            })
            .addCase(addPaymentSourceThunk.rejected, (state, action) => {
                state.error = action.payload as string || action.error.message || "";
                state.paymentLoader = false
            })
            .addCase(deletePaymentSourceThunk.pending, (state, action) => {
                state.paymentLoader = true
            })
            .addCase(deletePaymentSourceThunk.fulfilled, (state, action) => {
                state.paymentLoader = false
                state.success = action.payload.data.message
            })
            .addCase(deletePaymentSourceThunk.rejected, (state, action) => {
                state.error = action.payload as string || action.error.message || "";
                state.paymentLoader = false
            })
            .addCase(updatePaymentNameDescriptionThunk.pending, (state, action) => {
                state.paymentLoader = true
            })
            .addCase(updatePaymentNameDescriptionThunk.fulfilled, (state, action) => {
                state.success = action.payload.data.message
                state.paymentLoader = false
            })
            .addCase(updatePaymentNameDescriptionThunk.rejected, (state, action: any) => {
                console.log("rejected", action)
                state.error = action.payload?.detail as string || action.error.message || "";
                state.paymentLoader = false
            })
            .addCase(addMakePaymentThunk.pending, (state, action) => {
                state.paymentLoader = true
            })
            .addCase(addMakePaymentThunk.fulfilled, (state, action) => {
                state.paymentLoader = false
                state.makePaymentSuccess = action.payload.data.success;
                action.payload.data.success ?
                    state.success = action.payload.data.message :
                    state.error = action.payload.data.message
            })
            .addCase(addMakePaymentThunk.rejected, (state, action: any) => {
                console.log("action error--", action.error, action.payload)
                state.error = action.payload?.details.response as string || action.error.message || "";
                state.paymentLoader = false
            })
            .addCase(addAutoPaymentThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(addAutoPaymentThunk.fulfilled, (state, action) => {
                state.loading = false
                state.success = action.payload.data.message
                state.error = ""
            })
            .addCase(addAutoPaymentThunk.rejected, (state, action) => {
                state.error = action.payload as string || action.error.message || "";
                state.loading = false
            })
            .addCase(deleteAutoPaymentThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(deleteAutoPaymentThunk.fulfilled, (state, action) => {
                state.loading = false
                action.payload.data.success ?
                    state.success = action.payload.data.message :
                    state.error = action.payload.data.message
            })
            .addCase(deleteAutoPaymentThunk.rejected, (state, action) => {
                state.error = action.payload as string || action.error.message || "";
                state.loading = false
            })
            .addCase(makeDefaultPaymentThunk.pending, (state, action) => {
                state.paymentLoader = true
            })
            .addCase(makeDefaultPaymentThunk.fulfilled, (state, action) => {
                state.paymentLoader = false
                state.success = action.payload.data.message
            })
            .addCase(makeDefaultPaymentThunk.rejected, (state, action) => {
                state.error = action.payload as string || action.error.message || "";
                state.paymentLoader = false
            })
    },
})

export const paymentActions = paymentSlice.actions
export default paymentSlice.reducer;